import React, { useEffect, useState } from 'react';
import { Box, Grid, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import makeStyles from '@mui/styles/makeStyles';
import AdminProductLinks from 'components/common/admin_product_links';
import ImageGallery from 'components/common/image_gallery';
import Toast from 'components/common/toast';
import Backordered from 'components/pages/product/backordered';
import FinancingModal from 'components/pages/product/financing_modal';
import ProductOptions from 'components/pages/product/product_options';
import HardwareWithSoftwareAddOnOptions from 'components/pages/product/product_with_software_add_on/hardware_with_software_add_on_options';
import ProductBanner from 'components/common/banners/product_banner';
import QrModal from 'components/common/qr_modal';
import {
  STICKY_HEADER_OFFSET,
  onPageView,
} from 'components/pages/product/helpers';
import { checkAddOnsForSoftware } from 'components/pages/product/product_with_software_add_on/utils';

const useStyles = makeStyles((theme) => ({
  stickyGalleryContainer: {
    position: 'sticky',
    alignSelf: 'flex-start',
    // TODO(aiven@): this is the height of the sticky CTA subnav, figure out
    // a better way to do this...
    top: `${STICKY_HEADER_OFFSET}px`,
  },
  galleryRoot: {
    overflow: 'hidden',
    [theme.breakpoints.up('md')]: {
      borderBottomRightRadius: '11px',
      borderTopRightRadius: '11px',
    },
  },
  detailsContainer: {
    maxWidth: '500px',
    padding: theme.spacing(0, 4),
    [theme.breakpoints.down('md')]: {
      margin: 'auto',
      padding: theme.spacing(0, 2),
    },
  },
}));

const ProductConfiguration = (props) => {
  const {
    cart,
    current_user,
    product,
    backordered,
    alternatives = [],
    alternatives_metadata = {},
  } = props;

  const theme = useTheme();
  const classes = useStyles();
  const mobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();

  const [toast, setToast] = useState({});
  const [currentProduct, setCurrentProduct] = useState(product);
  const [financingModalOpen, setFinancingModalOpen] = useState(false);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState(null);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [galleryImages, setGalleryImages] = useState(
    currentProduct.images.gallery || [],
  );

  useEffect(() => {
    // Ensure that we have the latest product when swapping alternative products
    setGalleryImages(currentProduct.images.gallery || []);
  }, [currentProduct]);

  const handleToastClose = () => {
    setToast({ open: false });
  };

  // Track page view event
  useEffect(() => onPageView(currentProduct.tracking_data), [currentProduct]);

  const appendRelationImages = (relatedProductKey) => {
    const defaultImages = currentProduct.images.gallery || [];
    const relatedImages = currentProduct.images.relation?.[relatedProductKey];

    if (!relatedImages || relatedImages.length === 0) {
      setGalleryImages(defaultImages);
      setActiveImageIndex(0);
      return;
    }

    // the active image index should be set to the first related image
    setGalleryImages([...defaultImages, ...relatedImages]);
    setActiveImageIndex(defaultImages.length);
  };

  const productOptions = () => {
    // Hardware + Software Add-On
    if (checkAddOnsForSoftware(product)) {
      return (
        <HardwareWithSoftwareAddOnOptions
          cart={cart}
          currentProduct={currentProduct}
          alternatives={alternatives}
          currentAltMetadata={alternatives_metadata[currentProduct.product_key]}
          setCurrentProduct={setCurrentProduct}
          setFinancingModalOpen={setFinancingModalOpen}
          setToast={setToast}
        />
      );
    }

    // Regular Options
    return (
      <ProductOptions
        currentProduct={currentProduct}
        setCurrentProduct={setCurrentProduct}
        setFinancingModalOpen={setFinancingModalOpen}
        alternatives={alternatives}
        currentAltMetadata={alternatives_metadata[currentProduct.product_key]}
        addOns={currentProduct.add_ons}
        appendRelationImages={appendRelationImages}
        setToast={setToast}
        cart={cart}
      />
    );
  };

  return (
    <>
      <AdminProductLinks product={currentProduct} />
      {currentProduct.banner && (
        <ProductBanner
          type={currentProduct.banner.type}
          content={currentProduct.banner}
        />
      )}
      <Toast {...toast} onClose={handleToastClose} />
      <Grid container mt={{ md: 4 }} mb={8}>
        <Grid
          item
          container
          xs={12}
          md={6}
          justifyContent={'center'}
          flexDirection={'column'}
          alignItems={'center'}
          className={mobile ? '' : classes.stickyGalleryContainer}
        >
          <ImageGallery
            cart={cart}
            product={currentProduct}
            images={galleryImages}
            threeSixty={currentProduct.images.three_sixty}
            threeDimensionalModel={currentProduct.three_dimensional_model}
            activeImageIndex={activeImageIndex}
            setActiveImageIndex={setActiveImageIndex}
            aspectRatio={8 / 5}
            transitionDuration={400}
            setToast={setToast}
            setQrModalOpen={setQrModalOpen}
            setQrCodeUrl={setQrCodeUrl}
            classes={{ galleryRoot: classes.galleryRoot }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Box className={classes.detailsContainer}>
            {backordered ? (
              <Backordered
                currentProduct={currentProduct}
                backordered={backordered}
                setFinancingModalOpen={setFinancingModalOpen}
                setToast={setToast}
              />
            ) : (
              productOptions()
            )}
          </Box>
        </Grid>
      </Grid>
      <FinancingModal
        isLoggedIn={current_user.is_logged_in}
        open={financingModalOpen}
        handleClose={() => setFinancingModalOpen(false)}
      />
      <QrModal
        url={qrCodeUrl}
        footer={t('productPage.qrModal.footer')}
        open={qrModalOpen}
        handleClose={() => setQrModalOpen(false)}
      />
    </>
  );
};

export default ProductConfiguration;
